import React, { useEffect } from 'react';
import Head from 'next/head';
import { Provider, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { ToastContainer } from 'react-toastify';
import I18n from 'locales/i18n';
import store from 'store';
import useI18n from 'hooks/use-i18n';
import EN from 'locales/en.json';
import { APP_NAME_HEAD } from 'constants/constants';
import {
  ROUTE_LAYOUT,
  ROUTE_PRELOGIN_LAYOUT,
  ROUTE_AUTH_LAYOUT,
} from 'constants/route';

import { API_BASE_URL } from 'config';
import { SELECTED_CENTRES } from 'helpers/storage';
import Layout from 'components/Layout/Layout';
import AuthLayout from 'components/AuthLayout/AuthLayout';
import HeaderBeforeCms from 'components/LandingLayout/LandingLayoutCms';
import HeaderBefore from 'components/LandingLayout/LandingLayout';
import '@core/components/permission-button';
import '@core/components/ripple-button';
import { RefreshContextProvider } from 'contexts/RefreshContext';
import 'styles/globals.scss';
import 'antd/dist/antd.css';
import 'nprogress/nprogress.css';
import 'react-loading-skeleton/dist/skeleton.css';
import { isIOS, isMobile } from 'react-device-detect';

const I18Provider = ({ children }) => {
  const i18n = useI18n();
  useEffect(() => {
    i18n.locale('en', EN);
  }, []);
  return children;
};

function LMS(props) {
  const { Component, pageProps } = props;
  let vl = typeof window !== 'undefined' ? SELECTED_CENTRES.get() : null;
  const selectedCentre =
    typeof vl !== 'undefined' && vl !== null && vl !== ''
      ? JSON.parse(vl)
      : undefined;

  const { metaData } = pageProps;

  const favicon =
    selectedCentre?.favicon !== null &&
    selectedCentre?.favicon !== undefined &&
    selectedCentre?.favicon !== ''
      ? process.env.NEXT_PUBLIC_DOMAIN_IMG_HOST + selectedCentre?.favicon
      : metaData?.favicon?.root;

  const Sitename =
    selectedCentre?.centreSiteName || metaData?.name || APP_NAME_HEAD;
  const Sitedescription =
    selectedCentre?.centreSiteDescription || metaData?.description;
  const title = `${Sitename} ${Sitedescription ? '-' : ''} ${Sitedescription}`;

  const { pathname } = useRouter();
  const handleLayout = () => {
    if (ROUTE_LAYOUT.indexOf(pathname) !== -1) {
      return (
        <Layout>
          {/* <script>
            if (!crossOriginIsolated) SharedArrayBuffer = ArrayBuffer;
          </script>
          <noscript>You need to enable JavaScript to run this app.</noscript> */}
          <Component {...pageProps} />
        </Layout>
      );
    }
    if (ROUTE_AUTH_LAYOUT.indexOf(pathname) !== -1) {
      return (
        <AuthLayout>
          <Component {...pageProps} />
        </AuthLayout>
      );
    }
    //Before Login

    if (ROUTE_PRELOGIN_LAYOUT.indexOf(pathname) !== -1) {
      return (
        <>
          {metaData?.cmsHomePage == true ? (
            <HeaderBeforeCms>
              <Component {...pageProps} />
            </HeaderBeforeCms>
          ) : (
            <HeaderBefore>
              <Component {...pageProps} />
            </HeaderBefore>
          )}
        </>
      );
    }
    return <Component {...pageProps} />;
  };

  return (
    <Provider store={store}>
      <I18n lngDict={pageProps?.lngDict} locale={pageProps?.lng}>
        <I18Provider>
          <RefreshContextProvider>
            <Head>
              <title>{title}</title>
              <meta charSet="utf-8" />
              <meta
                name="zoom-domain-verification"
                content="ZOOM_verify_vqTmaKYlqXht8VnS5iq78U"
              />
              <link rel="shortcut icon" href={favicon} />
              <link rel="preconnect" href="https://fonts.gstatic.com" />
              <link
                href="https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap"
                rel="stylesheet"
              />
              <link
                href="https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,300;0,400;0,500;0,700;1,300;1,400&display=swap"
                rel="stylesheet"
              />
              <link
                href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300..800;1,300..800&family=Urbanist:ital,wght@0,100..900;1,100..900&display=swap"
                rel="stylesheet"
              ></link>
              <link
                rel="stylesheet"
                href="https://cdn.jsdelivr.net/npm/bootstrap@4.5.3/dist/css/bootstrap.min.css"
              />
              <link
                rel="stylesheet"
                href="https://fonts.googleapis.com/icon?family=Material+Icons"
              />
              <link
                rel="stylesheet"
                href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/4.7.0/css/font-awesome.min.css"
              />
              <link
                rel="stylesheet"
                href="//fonts.googleapis.com/css2?family=Material+Icons+Outlined"
              />
              <link
                rel="stylesheet"
                href="https://www.w3schools.com/w3css/4/w3.css"
              />
              <link
                rel="stylesheet"
                href="https://maxcdn.bootstrapcdn.com/bootstrap/4.5.2/css/bootstrap.min.css"
              />

              <script src="https://ajax.googleapis.com/ajax/libs/jquery/3.5.1/jquery.min.js"></script>
              <script src="https://maxcdn.bootstrapcdn.com/bootstrap/4.5.2/js/bootstrap.min.js"></script>
              <script src="https://cdnjs.cloudflare.com/ajax/libs/popper.js/1.16.0/umd/popper.min.js"></script>
              {/* <script>
                if (!crossOriginIsolated) SharedArrayBuffer = ArrayBuffer;
              </script>
              <noscript>
                You need to enable JavaScript to run this app.
              </noscript> */}
            </Head>
            <div className={`${isMobile && isIOS ? 'mobile-ios' : ''}`}>
              {handleLayout()}
            </div>
            <ToastContainer newestOnTop />
          </RefreshContextProvider>
        </I18Provider>
      </I18n>
    </Provider>
  );
}

LMS.getInitialProps = async (ctx) => {
  // Fetch data from external API
  try {
    const res = await fetch(`${API_BASE_URL}/settings/get-meta-data`);
    const metaData = await res.json();
    const update = {};
    metaData.payload.forEach((item) => {
      update[item.key] = item.value;
    });
    return { pageProps: { metaData: update } };
  } catch (error) {
    console.log(error);
    return { pageProps: { metaData: {} } };
  }
};

export default LMS;
